import React, { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import useSWR, { mutate as globalMutate } from "swr";
import {
  Clock,
  CheckCircle2,
  XCircle,
  FileText,
  LogOut,
  Download,
  Search,
  Loader2,
  X,
  RefreshCw,
} from "lucide-react";
import PageShell from "@/components/PageShell";
import Button from "@/components/Button";
import FormField from "@/components/FormField";
import { api, clearAuth, downloadCsv } from "@/lib/api";

import SchoolCard from "./components/SchoolCard";
import EntryCard from "./components/EntryCard";
import EntryDetailModal from "./components/EntryDetailModal";
import EditSchoolModal from "./components/EditSchoolModal";
import ConfirmStatusModal, { ConfirmDeleteModal } from "./components/ConfirmStatusModal";

const TABS = [
  { key: "pending", label: "Pending", Icon: Clock },
  { key: "approved", label: "Approved", Icon: CheckCircle2 },
  { key: "rejected", label: "Rejected", Icon: XCircle },
  { key: "entries", label: "Entries", Icon: FileText },
];

const POLL_MS = 15000;

function isAuthError(err) {
  const m = String(err?.message || "").toLowerCase();
  return m.includes("401") || m.includes("403") || m.includes("token") || m.includes("not authoriz");
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("pending");
  const [filterCity, setFilterCity] = useState("");
  const [filterGrade, setFilterGrade] = useState("");
  const [filterType, setFilterType] = useState("");
  const [appliedFilters, setAppliedFilters] = useState({});
  const [schoolSearch, setSchoolSearch] = useState("");

  const [activeEntry, setActiveEntry] = useState(null);
  const [editSchool, setEditSchool] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const handleSwrError = useCallback(
    (err) => {
      if (isAuthError(err)) {
        clearAuth();
        navigate("/admin/login", { replace: true });
      }
    },
    [navigate]
  );

  // SWR fetchers
  const { data: pending = [] } = useSWR("admin/pending", () => api.adminSchools("pending"), {
    refreshInterval: POLL_MS,
    onError: handleSwrError,
  });
  const { data: approved = [] } = useSWR("admin/approved", () => api.adminSchools("approved"), {
    refreshInterval: POLL_MS,
    onError: handleSwrError,
  });
  const { data: rejected = [] } = useSWR("admin/rejected", () => api.adminSchools("rejected"), {
    refreshInterval: POLL_MS,
    onError: handleSwrError,
  });

  const entriesKey = ["admin/entries", appliedFilters];
  const { data: entries = [], isLoading: entriesLoading } = useSWR(
    entriesKey,
    () => api.adminEntries(appliedFilters),
    { refreshInterval: POLL_MS, onError: handleSwrError }
  );

  const refreshAll = useCallback(() => {
    globalMutate("admin/pending");
    globalMutate("admin/approved");
    globalMutate("admin/rejected");
    globalMutate(entriesKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tabSchools = tab === "pending" ? pending : tab === "approved" ? approved : rejected;
  const filteredSchools = useMemo(() => {
    if (tab === "entries") return [];
    const q = schoolSearch.trim().toLowerCase();
    if (!q) return tabSchools;
    return tabSchools.filter((s) => {
      const hay = [s.school_name, s.city, s.state, s.coordinator_name, s.coordinator_email, s.coordinator_mobile]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [tabSchools, schoolSearch, tab]);

  const counts = {
    pending: pending.length,
    approved: approved.length,
    rejected: rejected.length,
    entries: entries.length,
  };

  const loading =
    tab === "entries" ? entriesLoading && entries.length === 0 : tabSchools === undefined;

  const onLogout = () => {
    clearAuth();
    toast.success("Signed out");
    navigate("/admin/login", { replace: true });
  };

  const onApplyFilters = () => {
    setAppliedFilters({
      city: filterCity || undefined,
      grade: filterGrade || undefined,
      entry_type: filterType || undefined,
    });
  };

  const requestStatusChange = (school, newStatus) => {
    if (newStatus === "pending") {
      doStatusChange(school.id, newStatus);
      return;
    }
    setConfirmAction({ school, newStatus });
  };

  const doStatusChange = async (id, newStatus) => {
    try {
      await api.adminUpdateSchoolStatus(id, newStatus);
      toast.success(
        newStatus === "approved"
          ? "School approved — email sent."
          : newStatus === "rejected"
          ? "School rejected — email sent."
          : "Reset to pending."
      );
      setConfirmAction(null);
      refreshAll();
    } catch (err) {
      toast.error(err.message || "Action failed");
    }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await api.adminDeleteSchool(confirmDelete.id);
      toast.success(`Deleted ${confirmDelete.school_name}`);
      setConfirmDelete(null);
      refreshAll();
    } catch (err) {
      toast.error(err.message || "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  const doDeleteEntry = async (entry) => {
    try {
      await api.adminDeleteEntry(entry.id);
      toast.success(`Deleted entry: ${entry.title}`);
      setActiveEntry(null);
      refreshAll();
    } catch (err) {
      toast.error(err.message || "Delete failed");
      throw err;
    }
  };

  const saveEdit = async (changes) => {
    try {
      await api.adminUpdateSchool(editSchool.id, changes);
      toast.success("School updated");
      setEditSchool(null);
      refreshAll();
    } catch (err) {
      toast.error(err.message || "Update failed");
    }
  };

  const onExport = async (kind) => {
    try {
      await downloadCsv(kind);
      toast.success(`Exported ${kind} CSV`);
    } catch (err) {
      toast.error(err.message || "Export failed");
    }
  };

  return (
    <PageShell testid="admin-dashboard-screen" showFooter={false}>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
          <div>
            <h1 className="text-2xl sm:text-3xl font-black text-sprouts-text">Admin Dashboard</h1>
            <p className="text-xs sm:text-sm text-sprouts-muted mt-0.5">
              Sprouts School Travel Blogging Competition 2026
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={refreshAll}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-white border border-sprouts-border text-xs font-bold text-sprouts-text hover:bg-slate-50"
              data-testid="admin-refresh"
            >
              <RefreshCw size={14} /> Refresh
            </button>
            <button
              type="button"
              onClick={onLogout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-sprouts-blush text-sprouts-secondary text-xs font-extrabold"
            >
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>

        <button
          type="button"
          onClick={() => onExport("schools")}
          data-testid="export-schools-btn"
          className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-sprouts-primary hover:bg-sprouts-primary-dark text-white text-sm font-extrabold rounded-full px-4 py-2.5 mb-5"
        >
          <Download size={14} />
          Export Schools CSV (with travel history)
        </button>

        {/* Counters */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
          {[
            { label: "Pending", value: counts.pending, color: "#FBBF24" },
            { label: "Approved", value: counts.approved, color: "#34D399" },
            { label: "Rejected", value: counts.rejected, color: "#FF6B6B" },
            { label: "Entries", value: counts.entries, color: "#3AB0FF" },
          ].map((c) => (
            <div
              key={c.label}
              className="bg-white rounded-2xl border-2 px-4 py-3"
              style={{ borderColor: `${c.color}55` }}
              data-testid={`counter-${c.label.toLowerCase()}`}
            >
              <div className="text-2xl font-black" style={{ color: c.color }}>{c.value}</div>
              <div className="text-[11px] text-sprouts-muted font-bold mt-0.5">{c.label}</div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-2 overflow-x-auto pb-2 mb-4">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              data-testid={`admin-tab-${t.key}`}
              className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-extrabold border transition-colors whitespace-nowrap ${
                tab === t.key
                  ? "bg-sprouts-primary text-white border-sprouts-primary"
                  : "bg-white text-sprouts-text border-sprouts-border"
              }`}
            >
              <t.Icon size={14} />
              {t.label}
            </button>
          ))}
        </div>

        {/* Filters / Search */}
        {tab === "entries" ? (
          <div className="grid sm:grid-cols-4 gap-3 items-end mb-4">
            <FormField
              placeholder="City"
              value={filterCity}
              onChange={(e) => setFilterCity(e.target.value)}
              data-testid="filter-city"
              className="mb-0"
            />
            <FormField
              placeholder="Grade"
              inputMode="numeric"
              value={filterGrade}
              onChange={(e) => setFilterGrade(e.target.value)}
              data-testid="filter-grade"
              className="mb-0"
            />
            <FormField
              placeholder="Type (Blog/Reel/Video/Photo Story)"
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              data-testid="filter-type"
              className="mb-0"
            />
            <div className="flex gap-2">
              <Button onClick={onApplyFilters} data-testid="filter-apply">Apply</Button>
              <Button variant="outline" onClick={() => onExport("entries")} data-testid="export-entries-btn">
                Export CSV
              </Button>
            </div>
          </div>
        ) : (
          <div className="relative mb-4">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-sprouts-muted" />
            <input
              type="text"
              value={schoolSearch}
              onChange={(e) => setSchoolSearch(e.target.value)}
              placeholder="Search by school name, city, coordinator or email…"
              data-testid="school-search"
              className="w-full rounded-full bg-white border border-sprouts-border pl-11 pr-10 py-3 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-sprouts-primary/40"
            />
            {schoolSearch && (
              <button
                type="button"
                onClick={() => setSchoolSearch("")}
                data-testid="school-search-clear"
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-slate-100"
              >
                <X size={16} className="text-sprouts-muted" />
              </button>
            )}
          </div>
        )}

        {/* Content list */}
        <div>
          {loading ? (
            <div className="flex items-center justify-center py-16" data-testid="admin-loading">
              <Loader2 size={28} className="animate-spin text-sprouts-primary" />
            </div>
          ) : tab === "entries" ? (
            entries.length === 0 ? (
              <div className="text-center py-16 text-sprouts-muted font-semibold" data-testid="empty-state">
                No entries match your filters.
              </div>
            ) : (
              entries.map((e) => <EntryCard key={e.id} entry={e} onOpen={() => setActiveEntry(e)} />)
            )
          ) : filteredSchools.length === 0 ? (
            <div className="text-center py-16 text-sprouts-muted font-semibold" data-testid="empty-state">
              {tabSchools.length === 0
                ? `No ${tab} schools yet.`
                : `No ${tab} schools match "${schoolSearch}".`}
            </div>
          ) : (
            filteredSchools.map((s) => (
              <SchoolCard
                key={s.id}
                school={s}
                tab={tab}
                onApprove={() => requestStatusChange(s, "approved")}
                onReject={() => requestStatusChange(s, "rejected")}
                onReset={() => requestStatusChange(s, "pending")}
                onEdit={() => setEditSchool(s)}
                onDelete={() => setConfirmDelete(s)}
              />
            ))
          )}
        </div>
      </div>

      {confirmAction && (
        <ConfirmStatusModal
          school={confirmAction.school}
          newStatus={confirmAction.newStatus}
          onCancel={() => setConfirmAction(null)}
          onConfirm={() => doStatusChange(confirmAction.school.id, confirmAction.newStatus)}
        />
      )}
      {confirmDelete && (
        <ConfirmDeleteModal
          school={confirmDelete}
          onCancel={() => setConfirmDelete(null)}
          onConfirm={doDelete}
          loading={deleting}
        />
      )}
      {activeEntry && <EntryDetailModal entry={activeEntry} onClose={() => setActiveEntry(null)} onDelete={doDeleteEntry} />}
      {editSchool && (
        <EditSchoolModal
          school={editSchool}
          onClose={() => setEditSchool(null)}
          onSave={saveEdit}
        />
      )}
    </PageShell>
  );
}
